import {
  ROUTE_DOCUMENT_DETAILS,
  ROUTE_MEDIA_DETAILS,
} from '@common-modules/routing';
import { TDOId } from '@common-modules/universal/models/Brands';
import VideoFileIcon from '@resources/images/VideoFileIcon.svg';
import AudioFileIcon from '@resources/images/AudioFileIcon.svg';
import ImageFileIcon from '@resources/images/ImageFileIcon.svg';
import FileIcon from '@resources/images/FileIcon.svg';
import { DOCUMENT_REDACTION_MIME_TYPES } from './constants';

interface VeritoneFileMeta {
  mimetype?: string | null;
  /** Some payloads use camelCase `mimeType` instead of `mimetype`. */
  mimeType?: string | null;
  fileType?: string | null;
}

/**
 * Minimal structural shape shared by the case-folder file model and
 * `SummaryTDO` — enough to resolve a file's MIME type and route/icon.
 */
export interface MediaFileLike {
  primaryAsset?: { contentType?: string | null } | null;
  contentType?: string | null;
  details?: { veritoneFile?: VeritoneFileMeta | null } | null;
  veritoneFile?: VeritoneFileMeta | null;
}

/**
 * Resolve a file's MIME type, most-authoritative source first:
 *   1. primaryAsset.contentType — the ingested asset's actual MIME
 *   2. veritoneFile.mimetype    — MIME of the converted asset
 *   3. veritoneFile.fileType    — MIME of the original file (name is
 *                                 misleading; it carries a MIME, not a category)
 *   4. file.contentType         — URL-derived hint set at TDO create
 */
export const resolveFileType = (
  file?: MediaFileLike | null
): string | undefined => {
  const details = file?.details || file;
  return (
    file?.primaryAsset?.contentType ||
    // `mimetype` is the usual key, but some payloads carry the camelCase
    // `mimeType` instead — check both before falling back to fileType.
    details?.veritoneFile?.mimetype ||
    details?.veritoneFile?.mimeType ||
    details?.veritoneFile?.fileType ||
    file?.contentType ||
    undefined
  );
};

/**
 * PDFs open in the dedicated document editor route; every other media type
 * (video / audio / image) flows through the legacy media-details route.
 *
 * Resolve the MIME through `resolveFileType` rather than reading
 * `primaryAsset.contentType` directly: some entry points (e.g. the MainPage
 * grid's TDO list) don't carry a `primaryAsset` at all and only expose
 * `details.veritoneFile.mimetype`. Going through the shared resolver keeps the
 * routing decision consistent across the case folders, the media drawer, and
 * the grid regardless of which field is populated.
 */
export const isPdfFile = (file?: MediaFileLike | null): boolean =>
  isPdfMimeType(resolveFileType(file));

/**
 * PDF check for entry points that only have a raw MIME string (search
 * results, legacy media), rather than a full file object. Same test
 * `isPdfFile` applies after resolving the file's MIME.
 */
export const isPdfMimeType = (mime?: string | null): boolean =>
  mime?.startsWith('application/pdf') ?? false;

/**
 * Returns the routing action for opening a case file: the document editor for
 * PDFs, otherwise legacy media details. `caseId` rides along so the target page
 * can resolve case context (breadcrumbs, drawer, lock scope, audit attribution).
 */
export const fileRouteAction = ({
  file,
  tdoId,
  caseId,
}: {
  file?: MediaFileLike | null;
  tdoId: TDOId;
  caseId?: string;
}) =>
  isPdfFile(file)
    ? ROUTE_DOCUMENT_DETAILS({ tdoId, caseId })
    : ROUTE_MEDIA_DETAILS({ tdoId, caseId });

/**
 * The four redaction modes Redact gates independently (VE-21290). Each maps
 * to its own appConfig feature flag (`enable{Video,Audio,Image,Document}
 * Redaction`).
 */
export type RedactionMode = 'video' | 'audio' | 'image' | 'document';

/**
 * Classify a MIME type into the redaction mode that owns it, or `undefined`
 * when it isn't a redactable type. Accepts both concrete MIMEs ("image/jpeg")
 * and the wildcard forms used in file-input `accept` lists ("image/*").
 */
export const resolveRedactionMode = (
  mime?: string
): RedactionMode | undefined => {
  if (!mime) {
    return undefined;
  }
  const m = mime.split(';')[0]?.trim().toLowerCase() ?? '';
  if (m.startsWith('video/')) {
    return 'video';
  }
  if (m.startsWith('audio/')) {
    return 'audio';
  }
  if (m.startsWith('image/')) {
    return 'image';
  }
  if (
    (DOCUMENT_REDACTION_MIME_TYPES as readonly string[]).some(
      (t) => t.split(';')[0]?.trim().toLowerCase() === m
    )
  ) {
    return 'document';
  }
  return undefined;
};

/** The per-mode feature flags read from `selectFeatureFlags`. */
export interface RedactionModeFlags {
  enableVideoRedaction?: boolean;
  enableAudioRedaction?: boolean;
  enableImageRedaction?: boolean;
  enableDocumentRedaction?: boolean;
}

/**
 * Whether the redaction mode is enabled for the org. An unclassified type
 * (`undefined` mode) is treated as enabled — these gates only block known
 * redaction modes, never unknown/other content.
 */
export const isRedactionModeEnabled = (
  mode: RedactionMode | undefined,
  flags: RedactionModeFlags
): boolean => {
  switch (mode) {
    case 'video':
      return !!flags.enableVideoRedaction;
    case 'audio':
      return !!flags.enableAudioRedaction;
    case 'image':
      return !!flags.enableImageRedaction;
    case 'document':
      return !!flags.enableDocumentRedaction;
    default:
      return true;
  }
};

/** Which groups of media detection options the File Picker offers. */
export interface MediaDetectionOptions {
  /** Run Head / Person and Object Detection — they run on frames. */
  readonly objectDetection: boolean;
  /** Run Transcription, Speaker Separation and Voice Mask — they need audio. */
  readonly audio: boolean;
}

/**
 * The media detection options an org's redaction modes call for (VE-31061):
 * object detection for video or image, the audio options for audio or video.
 *
 * Fails open like the flags themselves: a mode counts as enabled unless it is
 * explicitly `false`, so a missing or not-yet-loaded config shows every
 * option rather than hiding one the org may need.
 */
export const getMediaDetectionOptions = (
  flags: RedactionModeFlags
): MediaDetectionOptions => {
  const video = flags.enableVideoRedaction !== false;
  return {
    objectDetection: video || flags.enableImageRedaction !== false,
    audio: video || flags.enableAudioRedaction !== false,
  };
};

export const SUPPORTED_FILE_TYPE_PREFIXES = [
  'video',
  'audio',
  'image',
  'application',
] as const;

export const isSupportedFileType = (contentType?: string | null): boolean =>
  new RegExp(`^(${SUPPORTED_FILE_TYPE_PREFIXES.join('|')})/`).test(
    contentType ?? ''
  );

/** True when the MIME maps to a media kind or a supported document type. */
export const isRecognisedFileType = (fileType?: string): boolean =>
  !!fileType &&
  (fileType.includes('video') ||
    fileType.includes('audio') ||
    fileType.includes('image') ||
    (DOCUMENT_REDACTION_MIME_TYPES as readonly string[]).some((m) =>
      fileType.includes(m)
    ));

/**
 * Render the file-kind icon for a MIME type. Documents (PDF and friends) fall
 * through to the generic file icon. `className` controls sizing per call site.
 */
export const getMediaFileIcon = (fileType?: string, className?: string) => {
  if (fileType?.includes('video')) {
    return (
      <img
        src={VideoFileIcon}
        data-testId="VideoFileIcon"
        className={className}
      />
    );
  }
  if (fileType?.includes('audio')) {
    return (
      <img
        src={AudioFileIcon}
        data-testId="AudioFileIcon"
        className={className}
      />
    );
  }
  if (fileType?.includes('image')) {
    return (
      <img
        src={ImageFileIcon}
        data-testId="ImageFileIcon"
        className={className}
      />
    );
  }
  return (
    <img
      src={FileIcon}
      data-testId="FileIcon"
      className={className}
      alt=""
      aria-hidden
    />
  );
};
